import { CustomerFormFields } from "@/components/customers/CustomerFormFields";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  createCustomer,
  customerErrorMessage,
  emptyCustomerDraft,
  isCustomerDraftComplete,
  normalizeCustomerDraft,
  validateCustomerDraft,
  type CustomerDraft,
  type CustomerRecord,
} from "@/lib/customers/customersStore";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (record: CustomerRecord) => void | Promise<void>;
  mode?: "full" | "quick";
  title?: string;
};

export function CreateCustomerDialog({
  open,
  onOpenChange,
  onCreated,
  mode = "full",
  title = "Add customer",
}: Props) {
  const [draft, setDraft] = useState<CustomerDraft>(() => emptyCustomerDraft());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(emptyCustomerDraft());
    setSaving(false);
  }, [open]);

  const save = async () => {
    if (saving) return;
    const validation = validateCustomerDraft(draft);
    if (!validation.valid) {
      toast.error(validation.message ?? "Complete all required customer fields.");
      return;
    }
    const payload = normalizeCustomerDraft(draft);
    setSaving(true);
    try {
      const { record, message } = await createCustomer(payload);
      toast.success(message);
      onOpenChange(false);
      await onCreated?.(record);
    } catch (error) {
      toast.error(customerErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={
          mode === "full"
            ? "max-h-[90vh] max-w-[min(96vw,960px)] overflow-y-auto sm:max-w-[960px]"
            : "max-h-[90vh] max-w-[min(96vw,32rem)] overflow-y-auto sm:max-w-lg"
        }
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <CustomerFormFields draft={draft} onChange={setDraft} mode={mode} disabled={saving} />
        <DialogFooter>
          <Button type="button" variant="glass" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="premium"
            onClick={() => void save()}
            disabled={saving || !isCustomerDraftComplete(draft)}
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              "Save customer"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
